import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';

import { upsertUserSettings } from '@modules/core/services';
import { UseMutationUserSettingsVariables, UserSettings, UserSettingsContext } from '@modules/core/types';
import { FETCH_DELAY } from '@modules/shared/constants';

import { CORE_GET_USER_SETTINGS_KEY, CORE_UPDATE_USER_SETTINGS_KEY } from '../constants';

function useMutationUserSettings(
  onSuccess: () => void,
  onError?: (
    error: unknown,
    variables: UseMutationUserSettingsVariables,
    context: UserSettingsContext | undefined,
  ) => void,
): UseMutationResult<string, unknown> {
  const queryClient = useQueryClient();

  const mutationFn = async ({ isNew, userId, ...rest }: UseMutationUserSettingsVariables): Promise<any> => {
    await upsertUserSettings(isNew, userId, rest);
  };

  const onMutateFn = async (updatedUserSettings: UserSettings): Promise<any> => {
    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({ queryKey: [CORE_UPDATE_USER_SETTINGS_KEY] });

    const previousUserSettings = queryClient.getQueryData([CORE_GET_USER_SETTINGS_KEY]) as UserSettings;

    // Optimistically update to the new value
    queryClient.setQueryData([CORE_UPDATE_USER_SETTINGS_KEY], updatedUserSettings);

    // Return a context object with the snapshotted value
    return { previousUserSettings };
  };

  const onErrorFn = (
    err: unknown,
    variables: UseMutationUserSettingsVariables,
    context: UserSettingsContext | undefined,
  ): void => {
    if (context === undefined) {
      return;
    }
    if (context.previousUserSettings !== undefined) {
      queryClient.setQueryData([CORE_UPDATE_USER_SETTINGS_KEY], context.previousUserSettings);
    }
    onError?.(err, variables, context);
  };

  const onSettledFn = async (): Promise<void> => {
    setTimeout(() => {
      void queryClient.invalidateQueries([CORE_GET_USER_SETTINGS_KEY]);
    }, FETCH_DELAY);
  };

  return useMutation<string, unknown, any, any>([CORE_UPDATE_USER_SETTINGS_KEY], mutationFn, {
    onMutate: onMutateFn,
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: onErrorFn,
    // Always refetch after error or success:
    onSettled: onSettledFn,
    onSuccess,
  });
}

export default useMutationUserSettings;
